import React, { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { claimPromptSlot } from '../utils/promptOncePerVisit';
import { colors as c } from '../theme';
import {
  detectDevice, detectBrowser, isStandalone, installGuideDue, markInstallGuideShown,
  cameFromOldAddress, acknowledgeOldAddress, currentInstallOffer, onInstallOffer,
  setInstallOffer, releaseTour, hasInstalledRecord, markInstalled, markIconRemoved,
} from '../utils/homeScreenGuide';

export function guideInstructions(device, browser) {
  const ios = device === 'iphone' || device === 'ipad';
  const fallback = browser === 'in-app' || browser === 'unknown' || (browser === 'edge' && ios);
  if (fallback) return { copy: true, label: `Open in ${ios ? 'Safari' : 'Chrome'}`, steps: [
    `Copy the link below, then open ${ios ? 'Safari' : 'Chrome'}.`,
    'Paste the link into the address bar and open it.',
    'Open Settings in Cipher to find this guide again.',
  ] };
  if (ios) return { steps: [
    browser === 'chrome' ? 'Tap Share beside the address bar.' : browser === 'firefox' ? 'Tap the Share icon in the address bar.' : 'Tap Share (box with an upward arrow). If needed, open the page menu first.',
    'Tap Add to Home Screen. Scroll down or tap View More if you do not see it.',
    'Keep the name Cipher, turn on Open as Web App if shown, then tap Add.',
  ] };
  if (browser === 'samsung') return { steps: ['Open the menu (three lines).', 'Choose Add page to, then Home screen.', 'Keep the name Cipher and confirm Add.'] };
  if (browser === 'firefox') return { steps: ['Open the three-dot menu.', 'Tap Install to open Add to Home Screen.', 'Tap Add automatically, or place the icon yourself.'] };
  if (device === 'android' && browser === 'edge') return { steps: ['Open the Edge menu (three dots).', 'Choose Install app or Add to phone. It can be on the second page of the menu.', 'Confirm, then open the new Cipher icon.'] };
  if (device === 'desktop') return { steps: ['Use the install icon in your browser’s address bar, if offered.', 'Choose Install. If no install option is offered, bookmark Cipher instead.'] };
  return { steps: ['Open the three-dot menu in Chrome.', 'Choose Install and create shortcut, then Install (or Add to home screen / Install app on older versions).', 'Confirm the browser’s install offer.'] };
}

// The Share button on iPhone and iPad: a box with an arrow pointing up out of it
export const ShareIcon = () => <svg data-testid="share-icon" aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-3px', marginLeft: 6 }}>
  <path d="M12 15V3M8 7l4-4 4 4M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" />
</svg>;

const button = { minHeight: 44, padding: '12px 16px', borderRadius: 12, border: `1px solid ${c.borderStrong}`, background: c.surface2, color: c.text, font: 'inherit', cursor: 'pointer' };
export default function PWAInstallGuide({ manual = false, onClose, ready = true, blocked = false, role = 'client' }) {
  const [show, setShow] = useState(manual);
  const [offer, setOffer] = useState(currentInstallOffer);
  const [notice, setNotice] = useState(cameFromOldAddress);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const finished = useRef(false);
  const dialog = useRef(null);
  const device = detectDevice();
  const browser = detectBrowser();
  const installed = isStandalone() || hasInstalledRecord();
  const native = Capacitor.isNativePlatform();
  const instructions = guideInstructions(device, browser);
  // Chrome and Edge on Android hand over the browser's own install offer
  const nativeOffer = device === 'android' && (browser === 'chrome' || browser === 'edge') && !!offer;
  const steps = nativeOffer ? ['Tap Install Cipher below.', 'Confirm the browser’s install offer, then open the new Cipher icon.'] : instructions.steps;
  const close = () => { finished.current = true; setShow(false); releaseTour(); onClose?.(); };

  useEffect(() => onInstallOffer(setOffer), []);
  useEffect(() => {
    if (notice && ready && !blocked && role === 'client' && !native) acknowledgeOldAddress();
  }, [notice, ready, blocked, role, native]);
  useEffect(() => {
    const done = () => { finished.current = true; setShow(false); releaseTour(); onClose?.(); };
    window.addEventListener('appinstalled', done);
    return () => window.removeEventListener('appinstalled', done);
  }, [onClose]);
  // Shown first, before the welcome tour, which waits for it (16 October 2026). Chrome's offer can
  // arrive after the screen opens, so this is decided again when it does.
  useEffect(() => {
    if (manual || finished.current || !ready || role == null) return undefined;
    if (blocked || native || installed || role !== 'client') {
      finished.current = true; setShow(false); releaseTour(); return undefined;
    }
    if (show) return undefined;
    if (!installGuideDue({ role, device })) { releaseTour(); return undefined; }
    const timer = setTimeout(() => {
      if (claimPromptSlot('install')) { markInstallGuideShown(); setShow(true); }
      else { finished.current = true; releaseTour(); }
    }, 1500);
    return () => clearTimeout(timer);
  }, [manual, ready, blocked, native, installed, role, device, show, offer]);
  useEffect(() => {
    if (!show) return undefined;
    const previous = document.activeElement;
    dialog.current?.focus();
    return () => previous?.focus?.();
  }, [show]);

  const install = async () => {
    if (!offer || busy) return;
    setBusy(true);
    try {
      await offer.prompt();
      const result = await offer.userChoice;
      setInstallOffer(null);
      if (result.outcome === 'accepted') { markInstalled(); close(); }
      else { setMessage('No problem. You can add Cipher later from Settings.'); }
    } catch { setInstallOffer(null); setMessage('Use your browser menu to add Cipher, or try again later.'); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    // Never copy query strings or fragments, which can carry account hand-off information.
    const link = `${window.location.origin}/`;
    try { await navigator.clipboard.writeText(link); setMessage('Link copied. Open your browser and paste it.'); }
    catch { setMessage(`Copy this address: ${link}`); }
  };
  const keyDown = e => {
    if (e.key === 'Escape') close();
    if (e.key !== 'Tab') return;
    const items = dialog.current.querySelectorAll('button:not(:disabled), a[href]');
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { e.preventDefault(); last?.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
  };
  if (role !== 'client' || native) return null;
  const oldNote = notice && ready && !blocked && <div role="status" style={{ padding: 16, background: c.surface1, color: c.text }}>
    <strong>Cipher is now one app.</strong> Fitness and Nutrition share the Cipher icon.
    <button style={{ ...button, marginLeft: 8 }} onClick={() => { acknowledgeOldAddress(); setNotice(false); }}>Got it</button>
  </div>;
  if (installed || !show) return oldNote || null;
  return <div style={{ position: 'fixed', inset: 0, zIndex: 9999, padding: 16, background: 'rgba(0,0,0,.82)', display: 'grid', placeItems: 'center' }}>
    <section ref={dialog} role="dialog" aria-modal="true" aria-labelledby="cipher-guide-title" tabIndex={-1} onKeyDown={keyDown}
      style={{ width: '100%', maxWidth: 390, maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto', boxSizing: 'border-box', outline: 'none', padding: 24, borderRadius: 20, border: `1px solid ${c.gold}`, background: c.surface1, color: c.text }}>
      <p style={{ color: c.gold, fontSize: 12, letterSpacing: 2, margin: '0 0 12px' }}>CIPHER · {device === 'ipad' ? 'iPad' : device === 'iphone' ? 'iPhone' : device === 'android' ? 'Android' : 'Computer'}</p>
      <h2 id="cipher-guide-title" style={{ fontSize: 25, lineHeight: 1.2, margin: '0 0 12px' }}>{instructions.label || 'Add Cipher to your home screen'}</h2>
      <p style={{ color: c.textMuted, lineHeight: 1.5 }}>{device === 'iphone' || device === 'ipad'
        ? 'Open Cipher from its icon for quick access, and for reminders if you choose to allow them.'
        : 'Keep your workouts and meals one tap away.'}</p>
      {browser === 'in-app' && <p>This link is open inside another app. Use your browser to add Cipher.</p>}
      {oldNote}
      <ol style={{ paddingLeft: 22, lineHeight: 1.5 }}>{steps.map(step => <li key={step} style={{ padding: '8px 0 8px 4px' }}>{step}{(device === 'iphone' || device === 'ipad') && /Share/.test(step) && <ShareIcon />}</li>)}</ol>
      {instructions.copy && <button style={{ ...button, width: '100%' }} onClick={copy}>Copy link</button>}
      {nativeOffer && <button style={{ ...button, width: '100%', background: c.gold, color: c.bg }} disabled={busy} onClick={install}>{busy ? 'Opening install offer…' : 'Install Cipher'}</button>}
      {!instructions.copy && device !== 'desktop' && !nativeOffer && <button style={{ ...button, width: '100%', background: c.gold, color: c.bg }} onClick={() => { markInstalled(); close(); }}>I've added Cipher</button>}
      <p role="status" style={{ fontSize: 14 }}>{message}</p>
      <button style={{ ...button, width: '100%' }} onClick={close}>{manual ? 'Done' : 'Not now'}</button>
      {!manual && <p style={{ color: c.textMuted, fontSize: 12, textAlign: 'center', marginBottom: 0 }}>Find these instructions any time in Settings.</p>}
    </section>
  </div>;
}

export function HomeScreenSettings({ role = 'client' }) {
  const [open, setOpen] = useState(false);
  const [, refresh] = useState(0);
  useEffect(() => onInstallOffer(() => refresh(n => n + 1)), []);
  if (role !== 'client' || Capacitor.isNativePlatform() || isStandalone()) return null;
  return <div style={{ marginBottom: 16 }}>
    {hasInstalledRecord()
      ? <button style={button} onClick={() => { markIconRemoved(); setOpen(true); }}>I removed my Cipher icon</button>
      : <button style={button} onClick={() => setOpen(true)}>Add Cipher to your home screen</button>}
    {open && <PWAInstallGuide manual onClose={() => setOpen(false)} role={role} />}
  </div>;
}
